<template lang="pug">
div
  //- Header
  v-row(no-gutters align="center")
    v-col
      .yz-heading-8.text-neutral-700 Medicine details
      .yz-regular-body-sm.text-neutral-500.mt-1 Setup your drug group, medicine dosage and instructions.
    v-col.shrink
      yz-btn(label="All branches" outlined size="lg" background-color="white")

  yz-divider.mt-4

  //- Tabs and search
  v-row.mt-4(no-gutters align="center")
    v-col(cols="12" md="8")
      yz-tabs(v-model="activeTab" type="line")
        yz-tab(label="Drug group")
        yz-tab(label="Instruction")
        yz-tab(label="Precaution")
        yz-tab(label="Frequency")
        yz-tab(label="Duration")
        yz-tab(label="Dosage unit")
        yz-tab(label="Indication")
    v-col(cols="12" md="4" align="end")
      .d-flex.align-center.justify-end
        yz-search-btn(v-model="searchText" @search="onSearch")
        yz-btn.ml-3(label="Add new" size="lg" @click="onAddNew")

  yz-divider

  //- Data table
  yz-data-table.mt-2(:headers="headers" :items="medications" no-outer-border radius="0" body-height="52px" header-height="36px" header-background-color="white" hide-default-footer :loading="isFetching" @click:row="onEditItem")
    template(v-slot:item.name="{ item }")
      .yz-regular-body-sm.text-neutral-700 {{ item.value }}
    template(v-slot:item.branches)
      yz-badge(border) All branches
    template(v-slot:item.status="{ item }")
      div(@click.stop)
        yz-switch(v-model="item.enabled" size="md" @change="onUpdateStatus(item, item.enabled)")
    template(v-slot:item.actions="{ item, index }")
      .d-flex.justify-center(@click.stop)
        yz-menu(offset-y :top="menuPositionTop(index)" left)
          template(#activator="{ on }")
            yz-btn(icon="mdi-dots-horizontal" size="sm" icon-size="24" background-color="transparent" icon-color="neutral-500" v-on="on")
          yz-list
            yz-list-item(@click="onEditItem(item)") Edit
            yz-divider
            yz-list-item(@click="onDeleteItem(item)")
              span.danger500--text Delete

  //- Pagination
  yz-pagination.mt-4(v-if="totalCount > pageSize" v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalCount" @input="onPageChanged")

  //- Dialogs
  add-group-dialog(v-if="openAddGroupsDialog" :open.sync="openAddGroupsDialog" :parentGroup="selectedMedication" @closed="closed" @reset="reset")
  add-instruction-dialog(v-if="openAddInstructionsDialog" :open.sync="openAddInstructionsDialog" :parentInstruction="selectedMedication" @closed="closed" @reset="reset")
  add-precaution-dialog(v-if="openAddPrecautionsDialog" :open.sync="openAddPrecautionsDialog" :parentPrecaution="selectedMedication" @closed="closed" @reset="reset")
  add-frequency-dialog(v-if="openAddFrequenciesDialog" :open.sync="openAddFrequenciesDialog" :parentFrequencies="selectedMedication" @closed="closed" @reset="reset")
  add-duration-dialog(v-if="openAddDurationsDialog" :open.sync="openAddDurationsDialog" :parentDuration="selectedMedication" @closed="closed" @reset="reset")
  add-dosage-dialog(v-if="openAddDosagesDialog" :open.sync="openAddDosagesDialog" :parentDosage="selectedMedication" @closed="closed" @reset="reset")
  add-indication-dialog(v-if="openAddIndicationDialog" :open.sync="openAddIndicationDialog" :parentIndication="selectedMedication" @closed="closed" @reset="reset")
  delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="closed" @delete="onConfirmDelete")
    .yz-heading-8 Are you sure you want to delete this item?
    .yz-regular-body-sm.mt-4 This action will be done immediately and can't be undone. Click "Confirm" to proceed
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddGroupDialog from '@/components/clinic/settings/AddGroupDialog.vue'
import AddInstructionDialog from '@/components/clinic/settings/AddInstructionDialog.vue'
import AddPrecautionDialog from '@/components/clinic/settings/AddPrecautionDialog.vue'
import AddFrequencyDialog from '@/components/clinic/settings/AddFrequencyDialog.vue'
import AddDurationDialog from '@/components/clinic/settings/AddDurationDialog.vue'
import AddDosageDialog from '@/components/clinic/settings/AddDosageDialog.vue'
import AddIndicationDialog from '@/components/clinic/settings/AddIndicationDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

const TAB_FIELDS = [
  'medication.group',
  'medication.instruction',
  'medication.precaution',
  'medication.frequency',
  'medication.duration',
  'medication.dosage',
  'medication.indication'
]

export default {
  components: { AddGroupDialog, AddInstructionDialog, AddPrecautionDialog, AddFrequencyDialog, AddDurationDialog, AddDosageDialog, AddIndicationDialog, DeleteConfirmationDialog },
  data: () => ({
    isFetching: false,
    isLoading: false,
    activeTab: 0,
    pageSize: 20,
    currentPage: 1,
    searchText: '',
    totalCount: 0,
    medications: [],
    selectedMedication: null,
    openAddGroupsDialog: false,
    openAddInstructionsDialog: false,
    openAddPrecautionsDialog: false,
    openAddFrequenciesDialog: false,
    openAddDurationsDialog: false,
    openAddDosagesDialog: false,
    openAddIndicationDialog: false,
    openDeleteDialog: false,
    headers: [
      { text: 'NAME', value: 'name', sortable: false },
      { text: 'BRANCHES', value: 'branches', sortable: false },
      { text: 'STATUS', value: 'status', width: '100', sortable: false },
      { text: 'ACTION', value: 'actions', width: '80', sortable: false, align: 'end' }
    ]
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' })
  },
  watch: {
    activeTab () {
      this.searchText = ''
      this.currentPage = 1
      this.fetchMedicationList()
    },
    pageSize () {
      this.currentPage = 1
      this.fetchMedicationList()
    }
  },
  mounted () {
    this.fetchMedicationList()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchMedicationList () {
      this.isFetching = true
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        company: this.company.id,
        field: TAB_FIELDS[this.activeTab]
      }
      if (this.searchText) { params.value__contains = this.searchText }
      this.$axios.get('/options/', { params })
        .then(resp => {
          this.medications = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    onSearch () {
      this.currentPage = 1
      this.fetchMedicationList()
    },
    onUpdateStatus (item, status) {
      this.isFetching = true
      this.$axios.patch(`/options/${item.id}`, { enabled: status })
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isFetching = false
        })
    },
    onConfirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/options/${this.selectedMedication.id}`)
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onAddNew () {
      if (this.activeTab === 0) { this.openAddGroupsDialog = true }
      if (this.activeTab === 1) { this.openAddInstructionsDialog = true }
      if (this.activeTab === 2) { this.openAddPrecautionsDialog = true }
      if (this.activeTab === 3) { this.openAddFrequenciesDialog = true }
      if (this.activeTab === 4) { this.openAddDurationsDialog = true }
      if (this.activeTab === 5) { this.openAddDosagesDialog = true }
      if (this.activeTab === 6) { this.openAddIndicationDialog = true }
    },
    onEditItem (item) {
      this.selectedMedication = item
      if (this.activeTab === 0) { this.openAddGroupsDialog = true }
      if (this.activeTab === 1) { this.openAddInstructionsDialog = true }
      if (this.activeTab === 2) { this.openAddPrecautionsDialog = true }
      if (this.activeTab === 3) { this.openAddFrequenciesDialog = true }
      if (this.activeTab === 4) { this.openAddDurationsDialog = true }
      if (this.activeTab === 5) { this.openAddDosagesDialog = true }
      if (this.activeTab === 6) { this.openAddIndicationDialog = true }
    },
    onDeleteItem (item) {
      this.selectedMedication = item
      this.openDeleteDialog = true
    },
    onPageChanged () {
      this.fetchMedicationList()
    },
    reset () {
      this.currentPage = 1
      this.fetchMedicationList()
      this.selectedMedication = null
      this.openDeleteDialog = false
      this.openAddGroupsDialog = false
      this.openAddInstructionsDialog = false
      this.openAddPrecautionsDialog = false
      this.openAddFrequenciesDialog = false
      this.openAddDurationsDialog = false
      this.openAddDosagesDialog = false
      this.openAddIndicationDialog = false
    },
    closed () {
      this.selectedMedication = null
      this.openDeleteDialog = false
      this.openAddGroupsDialog = false
      this.openAddInstructionsDialog = false
      this.openAddPrecautionsDialog = false
      this.openAddFrequenciesDialog = false
      this.openAddDurationsDialog = false
      this.openAddDosagesDialog = false
      this.openAddIndicationDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 6) {
        let start = this.medications.length - 2
        if (position > start) {
          return true
        }
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
</style>
